import React from 'react'

import {
  WppIconAsk,
  WppIconMore,
  WppIconSort,
  WppIconTick,
  WppTag,
  WppTypography,
} from '@platform-ui-kit/components-library-react'
import { ColDef, IHeaderParams } from 'ag-grid-community'
import { TFunction } from 'i18next'

import styles from '../pages/TablePreview/TablePreview.module.scss'

interface PreviewRow {
  readonly id: number
  readonly account: string
  readonly accountNumber: string
  readonly status: 'positive' | 'negative' | 'neutral' | 'warning'
  readonly statusText: string
  readonly agency?: string
  readonly market?: string
  readonly client?: string
  readonly mappedBy?: string
  readonly date?: string
  readonly dashboard: boolean
}

interface NavigationItem {
  readonly label: string
  readonly value: string
  readonly children?: NavigationItem[]
}

interface BreadcrumbItem {
  readonly label: string
}

interface PageTab {
  readonly id: string
  readonly title: string
}

interface ChartConfig {
  readonly [key: string]: unknown
}

interface TableDefaultColConfig {
  readonly headerComponent: (props: IHeaderParams) => React.JSX.Element
  readonly sortable: boolean
  readonly cellRenderer: ({ value }: { value: string }) => React.JSX.Element
}

interface PreviewConfig {
  readonly pagePreviewTabs: PageTab[]
  readonly pagePreviewBreadcrumbs: BreadcrumbItem[]
  readonly tablePreviewColumnDefs: ColDef[]
  readonly tablePreviewTableDefaultColConfig: TableDefaultColConfig
  readonly tablePreviewTableDisplayData: PreviewRow[]
  readonly pagePreviewNavigation: NavigationItem[]
  readonly formPreviewNavigation: NavigationItem[]
  readonly dataVizPreviewBarChartConfig: ChartConfig
  readonly dataVizPreviewAreaChartConfig: ChartConfig
}

const createTablePreviewTableInitialData = ({ t }: { t: TFunction }): PreviewRow[] => [
  {
    id: 1,
    account: t('table.sampleData.accountOne'),
    accountNumber: '123-423-4532',
    status: 'positive',
    statusText: t('table.rows.mapped'),
    agency: t('table.sampleData.agencyOne'),
    market: 'GE',
    client: t('table.sampleData.clientBmw'),
    mappedBy: t('table.sampleData.mappedByOne'),
    date: '2022-05-11',
    dashboard: true,
  },
  {
    id: 2,
    account: t('table.sampleData.accountTwo'),
    accountNumber: '523-145-2355',
    status: 'negative',
    statusText: t('table.rows.inactive'),
    agency: t('table.sampleData.agencyTwo'),
    market: 'IN',
    client: t('table.sampleData.clientFord'),
    mappedBy: t('table.sampleData.mappedByTwo'),
    date: '2022-05-13',
    dashboard: false,
  },
  {
    id: 3,
    account: t('table.sampleData.accountThree'),
    accountNumber: '123-423-8732',
    status: 'neutral',
    statusText: t('table.rows.disabled'),
    agency: t('table.sampleData.agencyThree'),
    market: 'GE',
    client: t('table.sampleData.clientBmw'),
    mappedBy: t('table.sampleData.mappedByThree'),
    date: '2022-05-10',
    dashboard: true,
  },
  {
    id: 4,
    account: t('table.sampleData.accountFour'),
    accountNumber: '873-643-3943',
    status: 'warning',
    statusText: t('table.rows.unmapped'),
    agency: undefined,
    market: undefined,
    client: undefined,
    mappedBy: undefined,
    date: undefined,
    dashboard: false,
  },
]

export const getPreviewConfig = ({ t }: { t: TFunction }): PreviewConfig => {
  const tablePreviewTableInitialData: PreviewRow[] = createTablePreviewTableInitialData({ t })

  return {
    pagePreviewTabs: [
      { id: 'home', title: t('navigation.pageTabs.home') },
      { id: 'form', title: t('navigation.pageTabs.form') },
      { id: 'table', title: t('navigation.pageTabs.table') },
      { id: 'data-viz', title: t('navigation.pageTabs.dataViz') },
      { id: 'components', title: t('navigation.pageTabs.components') },
    ],
    pagePreviewBreadcrumbs: [
      { label: t('navigation.breadcrumbs.acme') },
      { label: t('navigation.breadcrumbs.us') },
      { label: t('navigation.breadcrumbs.umbrella') },
      { label: t('navigation.breadcrumbs.targetingApp') },
    ],
    tablePreviewColumnDefs: [
      {
        field: t('table.columns.account'),
        headerName: t('table.columns.account'),
        sortable: true,
        resizable: true,
        rowDrag: true,
        width: 320,
        headerCheckboxSelection: true,
        checkboxSelection: true,
        cellRenderer: ({ data }: { data: PreviewRow }) => (
          <div className={styles.flexColumn}>
            <WppTypography type="s-body">{data.account}</WppTypography>
            <WppTypography type="xs-body" style={{ color: 'grey' }}>
              {data.accountNumber}
            </WppTypography>
          </div>
        ),
      },
      {
        field: 'statusText',
        headerName: t('table.columns.status'),
        sortable: true,
        width: 100,
        cellRenderer: ({ data }: { data: PreviewRow }) => <WppTag label={data.statusText} variant={data.status} />,
      },
      {
        field: 'agency',
        headerName: t('table.columns.agency'),
        sortable: true,
        width: 120,
      },
      {
        field: 'market',
        headerName: t('table.columns.market'),
        sortable: true,
        width: 120,
        cellRenderer: ({ data }: { data: PreviewRow }) =>
          data.market && <WppTag label={data.market} variant="neutral" />,
      },
      {
        field: 'client',
        headerName: t('table.columns.client'),
        sortable: true,
        width: 100,
      },
      {
        field: 'mappedBy',
        headerName: t('table.columns.mappedBy'),
        sortable: true,
        width: 120,
      },
      {
        field: 'date',
        headerName: t('table.columns.date'),
        sortable: true,
        width: 110,
      },
      {
        field: 'dashboard',
        headerName: t('table.columns.dashboard'),
        width: 140,
        cellRenderer: ({ data }: { data: PreviewRow }) => data.dashboard && <WppIconTick />,
      },
      {
        field: 'more',
        headerName: t('table.columns.more'),
        width: 60,
        cellRenderer: () => <WppIconMore direction="horizontal" style={{ color: 'var(--wpp-primary-color-500)' }} />,
      },
    ],
    tablePreviewTableDefaultColConfig: {
      headerComponent: (props: IHeaderParams) => (
        <div className="ag-header-cell-comp">
          {props.displayName !== t('table.columns.more') && (
            <WppTypography type="s-strong">{props.displayName}</WppTypography>
          )}
          {props.displayName === t('table.columns.dashboard') && <WppIconAsk style={{ marginLeft: 8 }} />}
          {props.enableSorting && (
            <div className="ag-sorting">
              <WppIconSort
                className={`top-sort-icon ${
                  props.column?.isSortAscending() ? 'active' : props.column?.isSortNone() ? '' : 'not-active'
                }`}
              />
            </div>
          )}
        </div>
      ),
      sortable: false,
      cellRenderer: ({ value }: { value: string }) => <WppTypography type="s-body">{value}</WppTypography>,
    },
    tablePreviewTableDisplayData: [...Array(83)].map(
      (_, index: number) => tablePreviewTableInitialData[index % tablePreviewTableInitialData.length],
    ),
    pagePreviewNavigation: [
      {
        label: t('navigation.pagePreview.clientServices'),
        value: 'client-services',
      },
      {
        label: t('navigation.pagePreview.learning'),
        value: 'learning',
        children: [
          {
            label: t('navigation.pagePreview.learningOne'),
            value: 'learning-1',
          },
          {
            label: t('navigation.pagePreview.learningTwo'),
            value: 'learning-2',
          },
        ],
      },
      {
        label: t('navigation.pagePreview.marketplace'),
        value: 'marketplace',
      },
      {
        label: t('navigation.pagePreview.reporting'),
        value: 'reporting',
      },
      {
        label: t('navigation.pagePreview.devPortal'),
        value: 'dev-portal',
      },
    ],
    formPreviewNavigation: [
      {
        label: t('navigation.formPreview.catalogue'),
        value: 'catalogue',
      },
      {
        label: t('navigation.formPreview.favourites'),
        value: 'favourites',
      },
      {
        label: t('navigation.formPreview.myAgency'),
        value: 'my-agency',
      },
      {
        label: t('navigation.formPreview.contentManagement'),
        value: 'content-management',
      },
    ],
    dataVizPreviewBarChartConfig: {
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        containLabel: true,
      },
      xAxis: {
        type: 'value',
      },
      yAxis: {
        type: 'category',
        data: [
          t('dataViz.barChartLabels.tvAds'),
          t('dataViz.barChartLabels.seeingProductPackaging'),
          t('dataViz.barChartLabels.recommendation'),
          t('dataViz.barChartLabels.tvSponsorship'),
          t('dataViz.barChartLabels.socialBrandPage'),
          t('dataViz.barChartLabels.socialBrandPage'),
        ],
      },
      series: [
        {
          data: [
            { value: 231, itemStyle: { color: '#BC71FB' }, label: { show: true, position: 'right' } },
            { value: 157, itemStyle: { color: '#7B90DF' }, label: { show: true, position: 'right' } },
            { value: 108, itemStyle: { color: '#F48052' }, label: { show: true, position: 'right' } },
            { value: 138, itemStyle: { color: '#7B90DF' }, label: { show: true, position: 'right' } },
            { value: 187, itemStyle: { color: '#68A512' }, label: { show: true, position: 'right' } },
            { value: 146, itemStyle: { color: '#F48052' }, label: { show: true, position: 'right' } },
          ],
          type: 'bar',
        },
      ],
    },
    dataVizPreviewAreaChartConfig: {
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        containLabel: true,
      },
      xAxis: [
        {
          type: 'category',
          boundaryGap: false,
          data: ['0', '500', '1000', '1500', '2000', '2500', '3000'],
        },
      ],
      yAxis: [
        {
          type: 'value',
        },
      ],
      series: [
        {
          name: t('dataViz.areaChartSeries.email'),
          type: 'line',
          stack: 'Total',
          color: '#F3E6FE',
          areaStyle: { color: '#F3E6FE' },
          emphasis: { focus: 'series' },
          data: [120, 132, 101, 134, 90, 230, 210],
        },
        {
          name: t('dataViz.areaChartSeries.unionAds'),
          type: 'line',
          stack: 'Total',
          color: '#E6C9FD',
          areaStyle: { color: '#E6C9FD' },
          emphasis: { focus: 'series' },
          data: [220, 182, 191, 234, 290, 330, 310],
        },
        {
          name: t('dataViz.areaChartSeries.videoAds'),
          type: 'line',
          stack: 'Total',
          color: '#CE97FB',
          areaStyle: { color: '#CE97FB' },
          emphasis: { focus: 'series' },
          data: [150, 232, 201, 154, 190, 330, 410],
        },
        {
          name: t('dataViz.areaChartSeries.direct'),
          type: 'line',
          stack: 'Total',
          color: '#9E30F8',
          areaStyle: { color: '#9E30F8' },
          emphasis: { focus: 'series' },
          data: [320, 332, 301, 334, 390, 330, 320],
        },
        {
          name: t('dataViz.areaChartSeries.searchEngine'),
          type: 'line',
          stack: 'Total',
          color: '#4E058A',
          areaStyle: { color: '#4E058A' },
          emphasis: { focus: 'series' },
          data: [820, 932, 901, 934, 1290, 1330, 1320],
        },
      ],
    },
  }
}
